import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import {
  apiGetEcgDerivedOptions,
  apiGetEcgDerivedScpCodesOptions,
  apiGetEcgOptions,
  CustomECGStatement,
  DefaultECGStatement,
  ECGDerivedDataGroup,
  PermissionBit,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { ecgMeasModes, ecgQuality } from "../../../constants/enum-to-text";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { PatientSubpage } from "../common/patient-subpage";
import { CellAnnotationDetails } from "../ecg-annotation/cell-annotation-details";
import { ECGs } from "./ecg";
import { ECGPlot } from "./ecg-plot";

export const ECGTable = observer(function ECGTable(props: { app: AppModel }) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const [model] = useState(
    () => new ECGs([], patient.api.permissions, patient.api.requireUserUUID()),
  );
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const patientIds = [patient.patientId];
  const ecgQuery = useQuery(
    apiGetEcgOptions({ query: { patient_ids: patientIds, paginate: false } }),
  );
  const derivedQuery = useQuery(
    apiGetEcgDerivedOptions({
      query: {
        patient_ids: patientIds,
        paginate: false,
        data_groups: [
          ECGDerivedDataGroup["scp-custom"],
          ECGDerivedDataGroup["scp-default"],
          ECGDerivedDataGroup.hr,
        ],
      },
    }),
  );
  const scpQuery = useQuery(apiGetEcgDerivedScpCodesOptions());

  const derivedData = derivedQuery.data?.derived_data ?? [];
  const scpCodes: (DefaultECGStatement | CustomECGStatement)[] =
    scpQuery.data?.ecg_annotations ?? [];
  const loading =
    !model.initialized || derivedQuery.isPending || scpQuery.isPending;

  useEffect(() => {
    if (ecgQuery.data) model.updateData(ecgQuery.data.ecg_data);
  }, [ecgQuery.data, model]);

  const getAnnotationsForEcg = (ecgUuid: string) => {
    return derivedData.filter(
      (d) =>
        d.ecg_uuid === ecgUuid &&
        (d.data_group === ECGDerivedDataGroup["scp-custom"] ||
          d.data_group === ECGDerivedDataGroup["scp-default"]),
    );
  };

  const getHrForEcg = (ecgUuid: string) => {
    return derivedData.find(
      (d) => d.ecg_uuid === ecgUuid && d.data_group === ECGDerivedDataGroup.hr,
    );
  };

  return (
    <PatientSubpage title={t`ECG data`} loading={loading}>
      <ECGPlot uuid={selectedUuid} onClose={() => setSelectedUuid(null)} />

      <DataTable
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "recording", other: "recordings" })}`
        }
        columns={[
          {
            header: <Trans>Measured</Trans>,
            attr: "measurement_datetime",
            type: "datetime",
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Device</Trans>,
            attr: "measurement_device",
            type: "string",
            filter: {
              kind: "multi",
              group: t`ECG`,
              options: uniqueValueOptions("measurement_device"),
            },
          },
          {
            header: <Trans>Leads</Trans>,
            attr: "leads",
            type: "array",
          },
          {
            header: <Trans>Length</Trans>,
            attr: "length",
            type: "number",
            precision: 1,
          },
          {
            header: <Trans>Mode</Trans>,
            attr: "measurement_mode",
            type: "enum",
            editable: true,
            values: ecgMeasModes,
            filter: {
              kind: "multi",
              group: t`ECG`,
              options: new Map(Object.entries(ecgMeasModes)),
            },
          },
          {
            header: <Trans>Quality</Trans>,
            attr: "quality_flag",
            type: "enum",
            editable: true,
            values: ecgQuality,
            filter: {
              kind: "multi",
              group: t`ECG`,
              options: new Map(Object.entries(ecgQuality)),
            },
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            editable: true,
            multiline: true,
          },
          {
            header: <Trans>Annotations</Trans>,
            cell: ({ item }) => {
              const annotations = getAnnotationsForEcg(item.uuid);
              if (annotations.length === 0) return <span>—</span>;
              return (
                <div className="flex flex-col gap-1">
                  {annotations.map((annotation) => (
                    <CellAnnotationDetails
                      key={annotation.uuid}
                      code={annotation.measurement_type}
                      codeDetails={
                        (annotation.custom_statement ??
                          scpCodes.find(
                            (obj) => obj.code === annotation.measurement_type,
                          ) ?? {
                            code: annotation.measurement_type,
                            description: annotation.measurement_type,
                            statement_category: "unknown",
                          }) as DefaultECGStatement | CustomECGStatement
                      }
                    />
                  ))}
                </div>
              );
            },
          },
          {
            header: <Trans>HR</Trans>,
            cell: ({ item }) => {
              const hr = getHrForEcg(item.uuid);
              return (
                <span>
                  {hr?.measurement_value ? `${hr.measurement_value} bpm` : "—"}
                </span>
              );
            },
          },
          {
            header: <Trans>Plot</Trans>,
            requiredPermission: PermissionBit.ECG_RAW,
            cell: (cellProps) => (
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setSelectedUuid(cellProps.item.uuid)}
              >
                <Trans>Plot</Trans>
              </Button>
            ),
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("ecg", item.uuid, editedField);
          await Promise.all([
            ecgQuery.refetch(),
            derivedQuery.refetch(),
            scpQuery.refetch(),
          ]);
        }}
      />
    </PatientSubpage>
  );
});
